import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type FocusEvent,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { STEP_META } from '../../fixtures/onboarding';
import {
  chapterAriaLive,
  chapterByNumber,
  chapterKicker,
  onboardingTintVar,
} from '../../lib/onboardingChapters';
import { deriveDisplayName } from '../../lib/displayName';
import { useKeyboardReveal } from '../../lib/formKeyboard';
import { scrollRouteToTop } from '../../lib/scrollRouteToTop';
import { TayroMark } from '../brand/TayroMark';
import { AccountMenu } from '../firstPlan/FirstPlanShell.AccountMenu';
import { ChapterSegments } from './ChapterSegments';
import { ActionDockProvider } from './onboardingV2';
import type { OnboardingShellProps } from './OnboardingShell';
import styles from './OnboardingShellV2.module.css';

/**
 * The shell under Part 7 (flag `onboarding_2_0_v1`), a sibling of the shell that
 * shipped. Same props, same copy resolution, same announced sentence, same focus
 * move; what changes is the chrome (ratified 2026-10-24, `Part 7 Onboarding.dc.html`,
 * proposals P1 to P12):
 *
 *   · PHONE (below 1024). A 56 bar (Back, the mark centred, Optional and the
 *     account button), then the chapter block on the chapter's tint: kicker,
 *     name in DM Sans, the context line on a chapter's first question only, four
 *     segments, the count. Sign out sits behind the account button (B465).
 *   · THE DOCK (B470). The step's primary button and the reason line over it are
 *     PORTALED into a bar that is the last child of the column and `position:
 *     sticky; bottom: var(--kb-inset)`. Steps keep rendering `<StepCta>` where
 *     they always did; `StepCta` and `BarNote` find the two slots through
 *     `ActionDockProvider`. Sticky, not fixed: the bar stays in the flow, so it
 *     needs no padding trick to keep the last answer clear, and it never leaves
 *     an ancestor's `content-visibility` clip behind (the fixed-descendant rake).
 *   · THE KEYBOARD (P4). `useKeyboardReveal` (the chat and sheet signal: the
 *     plugin's `keyboardWillShow` on native, `visualViewport` on the web) publishes
 *     `--kb-inset`; the bar rides on it and the focused field is revealed above
 *     both the keyboard and the bar. The header is not sticky, so it scrolls away.
 *   · DESKTOP (1024 and up). The tinted panel on the left carries the chapter and,
 *     at its foot, Sign out. Continue sits in the flow, under the answers, as in
 *     the code today; no dock is provided there.
 */

/** True when the document is scrolled down from the top. */
function pageIsScrolled(): boolean {
  if (typeof document === 'undefined') return false;
  const scroller = document.scrollingElement ?? document.documentElement;
  return (scroller?.scrollTop ?? 0) > 0 || (document.body?.scrollTop ?? 0) > 0;
}

/** A field the keyboard opens for. A select opens a wheel, and a checkbox or a
 *  radio opens nothing, so none of those register. */
function opensKeyboard(el: EventTarget | null): el is HTMLElement {
  if (!(el instanceof HTMLElement)) return false;
  if (el instanceof HTMLTextAreaElement) return true;
  if (!(el instanceof HTMLInputElement)) return false;
  return !['checkbox', 'radio', 'button', 'submit', 'range', 'file', 'color'].includes(el.type);
}

/** The reason Sign out is unavailable while the under-13 removal is running. */
export const SIGN_OUT_WAIT_SENTENCE = 'You can sign out once the removal finishes.';

export function OnboardingShellV2({
  current,
  onBack,
  optional,
  skip,
  children,
  metaOverride,
  chapter,
  signOutDisabled,
  headerNameInBody,
}: OnboardingShellProps) {
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const chapterMeta = chapter ? chapterByNumber(chapter.n) : null;
  // Same priority as the shell that shipped: an explicitly framed screen, then the
  // chapter, then STEP_META (defensive; no route path reaches it).
  const meta =
    metaOverride ??
    (chapterMeta
      ? {
          stepLabel: chapterKicker(chapterMeta.n),
          label: chapterMeta.name,
          context: chapterMeta.context,
          ambientBg: chapterMeta.ambientBg,
        }
      : { ...STEP_META[current - 1], stepLabel: '' });
  const liveMessage = chapter ? chapterAriaLive(chapter) : meta.label;
  const tint = onboardingTintVar(meta.ambientBg);
  // Ruling 7: on a phone the context line shows on a chapter's FIRST question only
  // (it turns with the chapter). A screen with its own framing (the doorstep, the
  // parent beat) and the desktop panel always show it.
  const showContext =
    isDesktop ||
    !chapter ||
    !!metaOverride ||
    chapter.questionIndex == null ||
    chapter.questionIndex === 1;

  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const { signOut, user: authUser } = useAuthContext();
  const navigate = useNavigate();
  const handleSignOut = async () => {
    try {
      await signOut();
    } catch {
      /* Supabase clears local state regardless */
    }
    navigate('/landing');
  };
  const onSignOutClick = () => {
    if (signOutDisabled) return;
    void handleSignOut();
  };
  const accountName = deriveDisplayName(authUser, null);

  // Programmatic focus on the heading when the screen changes, and (B465) a new
  // screen opens at the top of the page. Same trigger as the shell that shipped.
  const focusedOnceRef = useRef(false);
  useEffect(() => {
    if (focusedOnceRef.current && pageIsScrolled()) scrollRouteToTop();
    focusedOnceRef.current = true;
    headingRef.current?.focus({ preventScroll: true });
  }, [current, liveMessage]);

  // ── The dock: two slots the step's button and its reason line are portaled to.
  const [ctaEl, setCtaEl] = useState<HTMLDivElement | null>(null);
  const [noteEl, setNoteEl] = useState<HTMLDivElement | null>(null);
  const dockValue = useMemo(
    () => (isDesktop ? null : { ctaEl, noteEl }),
    [isDesktop, ctaEl, noteEl],
  );
  const dockRef = useRef<HTMLDivElement | null>(null);
  const [dockHeight, setDockHeight] = useState(96);
  useLayoutEffect(() => {
    const el = dockRef.current;
    if (!el) return undefined;
    const measure = (): void => {
      const h = Math.round(el.getBoundingClientRect().height);
      if (h > 0) setDockHeight(h);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [isDesktop]);

  // The keyboard (P4): the field is revealed above the keyboard AND the bar.
  const kb = useKeyboardReveal({ marginPx: dockHeight + 16 });
  const onFocusCapture = useCallback(
    (event: FocusEvent<HTMLElement>) => {
      if (isDesktop || !opensKeyboard(event.target)) return;
      kb.onFocus({ currentTarget: event.target });
    },
    [isDesktop, kb],
  );
  const onBlurCapture = useCallback(
    (event: FocusEvent<HTMLElement>) => {
      if (isDesktop) return;
      // Moving from one box to the next keeps the keyboard up: only a real leave clears.
      if (opensKeyboard(event.relatedTarget)) return;
      kb.onBlur();
    },
    [isDesktop, kb],
  );

  const bodyName = headerNameInBody ? (
    <h1 className={styles.bodyName} tabIndex={-1} ref={headingRef} key={meta.label}>
      {meta.label}
    </h1>
  ) : null;

  const headerText: ReactNode = (
    <>
      {meta.stepLabel && <div className={styles.kicker}>{meta.stepLabel}</div>}
      {!headerNameInBody && (
        <h1 className={styles.chName} tabIndex={-1} ref={headingRef} key={meta.label}>
          {meta.label}
        </h1>
      )}
      {showContext && meta.context && <p className={styles.ctx}>{meta.context}</p>}
    </>
  );

  const srLive = (
    <div className={styles.srOnly} aria-live="polite">
      {liveMessage}
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────
  // Desktop: the tinted panel and the page
  // ─────────────────────────────────────────────────────────────────────
  if (isDesktop) {
    return (
      <div className={styles.wrap} data-onb2="">
        {srLive}
        <div className={styles.desktop}>
          <aside className={styles.panel} style={{ background: tint }}>
            <div className={styles.panelLogo}>
              <TayroMark size={24} />
              <span>Tayro</span>
            </div>
            <div className={styles.panelMiddle}>{headerText}</div>
            <div className={styles.panelBottom}>
              {chapter && <ChapterSegments progress={chapter} />}
              {authUser && (
                <div className={styles.panelFoot}>
                  <button
                    type="button"
                    className={styles.panelSignOut}
                    onClick={onSignOutClick}
                    disabled={signOutDisabled}
                    aria-disabled={signOutDisabled || undefined}
                  >
                    Sign out
                  </button>
                  {signOutDisabled && <p className={styles.panelNote}>{SIGN_OUT_WAIT_SENTENCE}</p>}
                </div>
              )}
            </div>
          </aside>
          <main className={styles.page}>
            <div className={styles.pageTop}>
              {onBack ? (
                <button
                  type="button"
                  className={styles.backBtn}
                  onClick={onBack}
                  aria-label="Go back to previous step"
                >
                  <svg width="18" height="18" viewBox="0 0 14 14" fill="none" aria-hidden>
                    <path d="M9 11L5 7l4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Back
                </button>
              ) : (
                <span />
              )}
              <div className={styles.barRight}>
                {optional && <span className={styles.optionalBadge}>Optional</span>}
                {skip && (
                  <button type="button" className={styles.skipLink} onClick={skip.onClick}>
                    {skip.label}
                  </button>
                )}
              </div>
            </div>
            <div className={styles.pageInner}>
              {bodyName}
              {children}
            </div>
          </main>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────
  // Phone: the bar, the chapter block, the page, the dock
  // ─────────────────────────────────────────────────────────────────────
  return (
    <ActionDockProvider value={dockValue}>
      <div className={styles.wrap} data-onb2="">
        {srLive}
        <div className={styles.phone}>
          <header className={styles.header} style={{ background: tint }}>
            <div className={styles.bar}>
              <div className={styles.barLeft}>
                {onBack && (
                  <button
                    type="button"
                    className={styles.backBtn}
                    onClick={onBack}
                    aria-label="Go back to previous step"
                  >
                    <svg width="18" height="18" viewBox="0 0 14 14" fill="none" aria-hidden>
                      <path d="M9 11L5 7l4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Back
                  </button>
                )}
              </div>
              <span className={styles.brand} aria-hidden="true">
                <TayroMark size={24} />
                <span className={styles.brandWord}>Tayro</span>
              </span>
              <div className={styles.barRight}>
                {optional && <span className={styles.optionalBadge}>Optional</span>}
                {skip && (
                  <button type="button" className={styles.skipLink} onClick={skip.onClick}>
                    {skip.label}
                  </button>
                )}
                {authUser && (
                  <AccountMenu
                    name={accountName}
                    signOutDisabled={signOutDisabled}
                    waitSentence={SIGN_OUT_WAIT_SENTENCE}
                    onSignOut={onSignOutClick}
                  />
                )}
              </div>
            </div>
            <div className={styles.chBlock}>
              {headerText}
              {chapter && <ChapterSegments progress={chapter} />}
            </div>
          </header>

          <main
            className={styles.content}
            onFocusCapture={onFocusCapture}
            onBlurCapture={onBlurCapture}
          >
            {bodyName}
            {children}
          </main>

          <div className={styles.dock} ref={dockRef}>
            <div className={styles.dockNote} ref={setNoteEl} />
            <div className={styles.dockCta} ref={setCtaEl} />
          </div>
        </div>
      </div>
    </ActionDockProvider>
  );
}
